import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { PieChart } from "@mui/x-charts";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import AccidentIcon from "../../../icons/AccidentIcon";
import CallIcon from "../../../icons/CallIcon";
import CircleIcon from "../../../icons/CircleIcon";
import EditIcon from "../../../icons/EditIcon";
import EmptyIcon from "../../../icons/EmptyIcon";
import ExtraCalendarIcon from "../../../icons/ExtraCalendarIcon";
import FamilyIcon from "../../../icons/FamilyIcon";
import FemaleAvatar from "../../../icons/FemaleAvatar";
import MailIcon from "../../../icons/MailIcon";
import ProtectionIcon from "../../../icons/ProtectionIcon";
import TeleIcon from "../../../icons/TeleIcon";
import TickInCircleIcon from "../../../icons/TickInCircleIcon";
import { useUserStore } from "../../../stores/userStore";
import {
  ActivityData,
  LeadData,
  UpdateLeadDetailPayload,
} from "../../../types/leads.types";
import { formatDateToMMDDYYYY, formatTimeValue } from "../../../utils/date";

const SectionCard = ({
  children,
  p = 2,
  minHeight = 100,
  ...rest
}: React.PropsWithChildren<{ p?: number } & any>) => (
  <Box
    sx={{
      position: "relative",
      border: "1px solid #FAE4D3",
      borderRadius: "20px",
      backgroundColor: "#fff",
      minHeight,
      p,
    }}
    {...rest}
  >
    {children}
  </Box>
);

const CardHeader = ({
  icon,
  title,
  mb = 2,
  ml = 0,
  fontWeight = 600,
  color = "#183028",
}: {
  icon?: React.ReactNode;
  title: string;
  mb?: number;
  ml?: number;
  fontWeight?: number;
  color?: string;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb, ml }}>
    {icon}
    <Typography sx={{ fontSize: "0.92rem", fontWeight, color }}>
      {title}
    </Typography>
  </Box>
);

const IconBox = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      width: 42,
      height: 42,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {children}
  </Box>
);

interface Props {
  open: boolean;
  onClose: () => void;
  isDetailUpdating: boolean;
  leadData: LeadData | null | undefined;
  userInterestsData: any;
  editStatus: {
    code: string;
    shortDescription: string;
    longDescription: string;
  } | null;
  editNotes: string;
  onEditStatus: (val: {
    code: string;
    shortDescription: string;
    longDescription: string;
  }) => void;
  onEditNotes: (val: string) => void;
  customerStatusOptions: Array<{
    code: string;
    shortDescription: string;
    longDescription: string;
  }>;
  onUpdateLeadDetailSubmit: (data: UpdateLeadDetailPayload) => void;
  canEdit: boolean;
  activityData: ActivityData;
  originalRemarks?: string;
  originalCustomerResponse?: string;
  isLoadingUserInterestsData: boolean;
  isActivityLogLoading: boolean;
}

const ORANGE = "#E87722";
const ORANGE_HOVER = "#e67c00";
const BG_SOFT = "#FEF9F4";
const MUTED = "#6B7280";

const LeadDetailModal = ({
  open,
  onClose,
  isDetailUpdating,
  leadData,
  onEditStatus,
  onEditNotes,
  editStatus,
  editNotes,
  canEdit,
  customerStatusOptions,
  onUpdateLeadDetailSubmit,
  activityData,
  originalRemarks = "",
  originalCustomerResponse = "",
  userInterestsData,
  isLoadingUserInterestsData,
  isActivityLogLoading,
}: Props) => {
  const { t } = useTranslation();
  const { user } = useUserStore();

  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [tempNotes, setTempNotes] = useState(editNotes ?? "");
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(true);

  const isOverlayLoading = isLoadingUserInterestsData || isActivityLogLoading;

  useEffect(() => {
    setTempNotes(editNotes ?? "");
  }, [editNotes]);

  const lead = (leadData ?? {}) as LeadData;
  const hasChange = Boolean(editNotes?.trim() || editStatus);
  const updateDisabled = !hasChange || isDetailUpdating || !leadData;

  const getActionChipStyle = (action: string) => {
    switch ((action || "").toLowerCase()) {
      case "create":
        return {
          backgroundColor: "#e8f5e8",
          color: "#2e7d32",
          borderColor: "#2e7d32",
        };
      case "update":
        return {
          backgroundColor: "#e3f2fd",
          color: "#1976d2",
          borderColor: "#1976d2",
        };
      case "delete":
        return {
          backgroundColor: "#ffebee",
          color: "#d32f2f",
          borderColor: "#d32f2f",
        };
      default:
        return {
          backgroundColor: "#f5f5f5",
          color: "#666",
          borderColor: "#666",
        };
    }
  };

  const getProductChipStyle = (product: string) => {
    switch ((product || "").toLowerCase()) {
      case "interested":
        return {
          backgroundColor: "#FEF9F4",
          color: "#A25318",
          borderColor: "#F3BB90",
        };
      case "suggested":
        return {
          backgroundColor: "#FFFDF5",
          color: "#7F6921",
          borderColor: "#FEDF7A",
        };
      default:
        return {
          backgroundColor: "#f5f5f5",
          color: "#666",
          borderColor: "#666",
        };
    }
  };

  const getProductIcon = (label: string) => {
    const value = label.toLowerCase();

    if (value.includes("health")) {
      return <ProtectionIcon />;
    }

    if (value.includes("accident")) {
      return <AccidentIcon />;
    }

    if (value.includes("family")) {
      return <FamilyIcon />;
    }

    return <ProtectionIcon />;
  };

  const findCustomerStatusOptionDetail = (responseCode?: string) => {
    const details =
      customerStatusOptions.find(item => item.code === responseCode) || null;
    if (!details) return null;
    return {
      longDesc: details.longDescription,
      shortDesc: details.shortDescription,
    };
  };

  const onSubmit = () => {
    if (!leadData) return;
    setIsHistoryExpanded(true);
    onUpdateLeadDetailSubmit({
      id: leadData.id,
      remarks:
        editNotes && editNotes !== (originalRemarks ?? "") ? editNotes : "",
      customerResponse:
        editStatus?.code &&
        editStatus?.code !== (originalCustomerResponse ?? "")
          ? editStatus?.code
          : "",
      user: user?.name || "",
    });
  };

  const topics =
    Object.values(userInterestsData?.data ?? {})
      .map((entry: any) => entry?.contenthub_topics)
      .find(arr => Array.isArray(arr)) ?? [];

  const dowToWeekday = (dow?: number) => {
    if (!dow || dow < 1 || dow > 7) return "";
    const keys = [
      "dowMon",
      "dowTue",
      "dowWed",
      "dowThu",
      "dowFri",
      "dowSat",
      "dowSun",
    ];
    return t(`platform.${keys[dow - 1]}`);
  };

  const bestTimes =
    Object.values(userInterestsData?.data ?? {})
      .map((entry: any) => entry?.best_time_to_contact)
      .find((arr: any) => Array.isArray(arr)) ?? [];

  const score = lead?.leadScore ?? 9;

  const recentActivities = useMemo(() => {
    const list = activityData?.records ?? [];
    return list.slice(0, 5);
  }, [activityData]);

  const suggestedType = useMemo(() => {
    return lead?.suggestedType?.filter(item => item !== "Unknown");
  }, [lead]);

  const preferredProductTypes = useMemo(() => {
    return lead?.preferredProductTypes?.filter(item => item !== "Unknown");
  }, [lead]);

  const EmptyPlaceholder = (
    <Box
      sx={{
        width: "100%",
        height: 200,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <EmptyIcon />
      <Typography sx={{ mt: "14px", color: "#859D99" }}>
        {t("platform.noDataAvailable")}
      </Typography>
    </Box>
  );

  return (
    <Modal
      open={open}
      keepMounted
      onClose={() => {
        onClose();
        setIsHistoryExpanded(true);
      }}
      aria-labelledby="lead-detail-modal"
      aria-describedby="lead-detail-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "95%", md: "1180px" },
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: "16px",
          boxShadow: 24,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
        aria-busy={isOverlayLoading ? "true" : "false"}
      >
        {isOverlayLoading && (
          <Box
            role="progressbar"
            aria-label="Loading"
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.6)",
              backdropFilter: "blur(2px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10,
              pointerEvents: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
              }}
            >
              <CircularProgress size={36} sx={{ color: ORANGE }} />
              <Typography sx={{ color: "#555", fontSize: "0.9rem" }}>
                {t("platform.loading")}
              </Typography>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            p: "4px",
            px: 2,
            bgcolor: BG_SOFT,
            borderBottom: "1px solid #F3F5F4",
            display: "flex",
            alignItems: "center",
            flexDirection: "row-reverse",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton onClick={onClose} sx={{ color: "text.secondary" }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ p: 3, overflow: "auto", bgcolor: BG_SOFT }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "300px 1fr 260px",
              },
              gap: 2.5,
            }}
          >
            <SectionCard
              sx={{
                border: "1px solid #FAE4D3",
                borderRadius: "20px",
                overflow: "hidden",
                backgroundColor: "#fff",
                padding: 0,
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 2 }}
                >
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: "#FFE6CC",
                    }}
                  >
                    <FemaleAvatar />
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700 }}>
                      {lead?.fullName || ""}
                    </Typography>
                    <Typography sx={{ color: MUTED, fontSize: "0.85rem" }}>
                      {lead?.assignedChannel || lead?.channel || "—"}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    height: 200,
                    display: "flex",
                    position: "relative",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    backgroundImage: "url('/assets/images/leadModalBg.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  <Box sx={{ position: "relative", width: 100, height: 100 }}>
                    <Box
                      sx={{
                        position: "absolute",
                        left: 51,
                        top: 55,
                        width: 100,
                        height: 100,
                        transform: "translate(-50%, -50%)",
                        borderRadius: "50%",
                        bgcolor: "#fff",
                        zIndex: 0,
                        pointerEvents: "none",
                      }}
                    />

                    <PieChart
                      series={[
                        {
                          cx: 45,
                          cy: 50,
                          innerRadius: 25,
                          outerRadius: 45,
                          cornerRadius: 6,
                          paddingAngle: 0,
                          startAngle: -120,
                          endAngle: 120,
                          highlightScope: {
                            highlighted: "none",
                            faded: "none",
                          },
                          data: [
                            {
                              id: 0,
                              value: score,
                              label: t("platform.hotLeads"),
                              color: "#E87722",
                            },
                            {
                              id: 1,
                              value: score + 1,
                              label: t("platform.round"),
                              color: "#DBDFE1",
                            },
                          ],
                        },
                      ]}
                      width={100}
                      height={100}
                      hideLegend
                      tooltip={{ trigger: "none" }}
                      axisHighlight={{ x: "none", y: "none" }}
                      slotProps={{ legend: { hidden: true } }}
                      disableAxisListener
                      skipAnimation
                    />

                    <Typography
                      sx={{
                        position: "absolute",
                        left: `50px`,
                        top: `55px`,
                        transform: "translate(-50%, -50%)",
                        color: "#000",
                        fontSize: 20,
                        fontWeight: 600,
                        zIndex: 2,
                        pointerEvents: "none",
                        userSelect: "none",
                        lineHeight: 1,
                      }}
                    >
                      {score}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      left: 150,
                      top: 140,
                      width: 160,
                      height: 32,
                      transform: "translate(-50%, -50%)",
                      borderRadius: "20px",
                      bgcolor: "#fff",
                      fontSize: "12px",
                      fontWeight: 500,
                      color: ORANGE,
                      zIndex: 0,
                      pointerEvents: "none",
                    }}
                  >
                    {t("platform.hotLead")}
                  </Box>
                </Box>
              </Box>

              <Box sx={{ backgroundColor: "#FEF2E9" }}>
                <Box
                  sx={{
                    p: "18px",
                    borderTopLeftRadius: "20px",
                    borderTopRightRadius: "20px",
                    backgroundColor: "#fff",
                  }}
                >
                  <Box sx={{ display: "grid", rowGap: 1.5 }}>
                    <Box sx={{ display: "flex", gap: 1.25 }}>
                      <IconBox>
                        <CallIcon />
                      </IconBox>
                      <Box
                        sx={{
                          display: "flex",
                          height: "42px",
                          justifyContent: "center",
                          flexDirection: "column",
                          lineHeight: 1.2,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                        >
                          {t("platform.assignedTo")}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.95rem",
                            fontWeight: 450,
                            color: "text.primary",
                          }}
                        >
                          {lead?.assignedChannel || "—"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1.25 }}>
                      <IconBox>
                        <MailIcon />
                      </IconBox>
                      <Box
                        sx={{
                          display: "flex",
                          height: "42px",
                          justifyContent: "center",
                          flexDirection: "column",
                          lineHeight: 1.2,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                        >
                          {t("platform.emailAddress")}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.95rem",
                            fontWeight: 450,
                            color: "text.primary",
                          }}
                        >
                          {lead?.emailAddress || "—"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1.25 }}>
                      <IconBox>
                        <TeleIcon />
                      </IconBox>
                      <Box
                        sx={{
                          display: "flex",
                          height: "42px",
                          justifyContent: "center",
                          flexDirection: "column",
                          lineHeight: 1.2,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "text.secondary" }}
                        >
                          {t("platform.mobileNumber")}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.95rem",
                            fontWeight: 450,
                            color: "text.primary",
                          }}
                        >
                          {lead?.contactPhoneNumber || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Divider sx={{ my: 2, borderColor: "#F3F5F4" }} />

                  <>
                    <CardHeader
                      title={t("platform.bestTimeToContact")}
                      mb={1}
                      ml={1}
                      fontWeight={400}
                      color={"#636566"}
                    />
                    {!!bestTimes?.length ? (
                      <Box sx={{ display: "grid", rowGap: 1 }}>
                        {bestTimes.map(
                          (
                            item: {
                              dow?: number;
                              hour?: number;
                              num_event_days?: number;
                            },
                            idx: number,
                          ) => (
                            <Box
                              key={idx}
                              sx={{
                                display: "flex",
                                gap: 1,
                                backgroundColor: "#FEF9F4",
                                borderRadius: "16px",
                                padding: "4px 12px",
                                fontSize: "12px",
                              }}
                            >
                              <Box
                                key={idx}
                                sx={{
                                  display: "flex",
                                  gap: 1,
                                  backgroundColor: "#fff",
                                  borderRadius: "10px",
                                  padding: "6px 12px",
                                  fontSize: "12px",
                                }}
                              >
                                <Typography sx={{ color: "#E87722" }}>
                                  {dowToWeekday(item?.dow)}
                                </Typography>
                                {typeof item?.hour === "number" && (
                                  <Typography
                                    sx={{ color: "#E87722" }}
                                  >{`${String(item.hour).padStart(
                                    2,
                                    "0",
                                  )}:00`}</Typography>
                                )}
                              </Box>
                            </Box>
                          ),
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          marginTop: "12px",
                          height: "40px",
                          width: "100%",
                          borderRadius: "20px",
                          backgroundColor: "#F5F5F5",
                          display: "flex",
                          alignItems: "center",
                          padding: "0 20px",
                          color: "#859D99",
                          fontSize: "12px",
                        }}
                      >
                        {t("platform.noDataAvailable")}
                      </Box>
                    )}
                  </>
                </Box>
              </Box>
            </SectionCard>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2.5,
                width: 536,
              }}
            >
              <SectionCard minHeight={330}>
                <CardHeader title={t("platform.suggestedProducts")} mb={0.5} />
                <Typography sx={{ color: MUTED, fontSize: "0.85rem", mb: 2 }}>
                  {t("platform.forUser", {
                    user_name: lead?.fullName || "{user_name}",
                  })}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 0.5,
                    flexWrap: "wrap",
                    maxHeight: 236,
                    overflowY: "auto",
                    marginRight: "-14px",
                    paddingRight: "14px",
                  }}
                >
                  {!(suggestedType?.length || preferredProductTypes?.length) &&
                    EmptyPlaceholder}
                  {suggestedType?.map(item => (
                    <Box
                      key={item}
                      sx={{
                        width: "100%",
                        display: "grid",
                        gridTemplateColumns: "auto 20px 1fr auto",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      {getProductIcon(item)}

                      <Box />

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          noWrap
                          sx={{
                            fontWeight: 450,
                            fontSize: "0.9rem",
                            maxWidth: 320,
                          }}
                        >
                          {item}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          justifySelf: "end",
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Chip
                          label={t("platform.suggested")}
                          size="small"
                          sx={{
                            border: "1px solid black",
                            height: 22,
                            fontSize: "0.7rem",
                            borderRadius: "5px",
                            ...getProductChipStyle("Suggested"),
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                  {preferredProductTypes?.map(item => (
                    <Box
                      key={item}
                      sx={{
                        width: "100%",
                        display: "grid",
                        gridTemplateColumns: "auto 20px 1fr auto",
                        alignItems: "center",
                        marginBottom: "8px",
                      }}
                    >
                      {getProductIcon(item)}

                      <Box />

                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          noWrap
                          sx={{
                            fontWeight: 450,
                            fontSize: "0.9rem",
                            maxWidth: 320,
                          }}
                        >
                          {item}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          justifySelf: "end",
                          display: "flex",
                          justifyContent: "flex-end",
                        }}
                      >
                        <Chip
                          label={t("platform.interested")}
                          size="small"
                          sx={{
                            border: "1px solid black",
                            height: 22,
                            fontSize: "0.7rem",
                            borderRadius: "5px",
                            ...getProductChipStyle("Interested"),
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </SectionCard>

              <SectionCard minHeight={300}>
                <CardHeader
                  title={t("platform.recentActivitiesAndCampaigns")}
                  mb={0.5}
                />
                <Typography sx={{ color: MUTED, fontSize: "0.85rem", mb: 3 }}>
                  {lead?.assignedChannel || lead?.channel || "—"}
                </Typography>
                <Box
                  sx={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    marginRight: "-14px",
                    paddingRight: "14px",
                  }}
                >
                  {recentActivities?.length ? (
                    <Box sx={{ display: "grid", rowGap: 1.25 }}>
                      {recentActivities.map(activity => {
                        const responseData = findCustomerStatusOptionDetail(
                          activity.details.customerResponse,
                        );

                        return (
                          <>
                            <Box
                              key={activity.id}
                              sx={{
                                display: "grid",
                                position: "relative",
                                gridTemplateColumns: "64px 1fr auto",
                                alignItems: "center",
                                columnGap: 1,
                              }}
                            >
                              <ExtraCalendarIcon />
                              <Box
                                sx={{
                                  position: "absolute",
                                  justifySelf: "start",
                                  top: 8,
                                  left: -8,
                                  width: 64,
                                  height: 40,
                                  borderRadius: "10px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexDirection: "column",
                                  fontSize: "0.75rem",
                                }}
                              >
                                <Typography
                                  sx={{ fontWeight: 450, lineHeight: 1 }}
                                >
                                  {formatDateToMMDDYYYY(
                                    activity.createdDateTime,
                                  ).slice(0, 6)}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: "0.7rem", color: MUTED }}
                                >
                                  {formatTimeValue(activity.createdDateTime)}
                                </Typography>
                              </Box>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  noWrap
                                  sx={{
                                    fontWeight: 450,
                                    fontSize: "0.9rem",
                                    maxWidth: 350,
                                  }}
                                  title={
                                    responseData?.longDesc ||
                                    activity.action ||
                                    t("platform.activity")
                                  }
                                >
                                  {responseData?.longDesc ||
                                    activity.action ||
                                    t("platform.activity")}
                                </Typography>

                                <Typography
                                  sx={{ color: MUTED, fontSize: "0.8rem" }}
                                >
                                  {responseData?.shortDesc ||
                                    activity.details?.remarks ||
                                    "-"}
                                </Typography>
                              </Box>
                              <Chip
                                label={activity.action}
                                size="small"
                                sx={{
                                  border: "1px solid black",
                                  height: 22,
                                  fontSize: "0.7rem",
                                  justifySelf: "end",
                                  borderRadius: "5px",
                                  ...getActionChipStyle(activity.action),
                                }}
                              />
                            </Box>
                            <Divider sx={{ borderColor: "#F3F5F4" }} />
                          </>
                        );
                      })}
                    </Box>
                  ) : (
                    EmptyPlaceholder
                  )}
                </Box>
              </SectionCard>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {/* {(!!topics.length) && (
                <SectionCard>
                  <CardHeader
                    title="AI Intelligence"
                  />
                  {!!topics.length &&
                    topics.map((topic: any, idx: number) => (
                      <Box sx={{ mb: 1 }} key={`topic-${idx}`}>
                        <Typography
                          sx={{ color: MUTED, fontSize: "0.85rem", mb: 0.5 }}
                        >
                          {topic?.topic_name ?? ""}
                        </Typography>
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {(topic?.read_content ?? [])
                            .flatMap((rc: any) => rc?.keywords ?? [])
                            .filter((kw: string) => !!kw && kw.trim() !== "")
                            .map((kw: string, k: number) => (
                              <Chip
                                key={k}
                                label={`#${kw}`}
                                size="small"
                                sx={{
                                  backgroundColor: "transparent",
                                  color: ORANGE,
                                  borderRadius: "8px",
                                  fontWeight: 500,
                                  border: `1px solid ${ORANGE}33`,
                                  height: 24,
                                }}
                              />
                            ))}
                        </Box>
                      </Box>
                    ))}
                </SectionCard>
              )} */}

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2.5,
                  width: 260,
                }}
              >
                <SectionCard minHeight={330}>
                  <CardHeader title={t("platform.stageUpdate")} />

                  <FormControl fullWidth sx={{ mb: 1.5 }}>
                    <Select
                      value={editStatus?.code || ""}
                      disabled={!canEdit}
                      onChange={e => {
                        const selectedValue = e.target.value as string;
                        const selectedOption = customerStatusOptions.find(
                          opt => opt.code === selectedValue,
                        );
                        if (selectedOption) onEditStatus(selectedOption);
                      }}
                      displayEmpty
                      IconComponent={ExpandMoreIcon}
                      sx={{
                        backgroundColor: "#f9f9f9",
                        borderRadius: "12px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#e0e0e0",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#d0d0d0",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: ORANGE,
                        },
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                          padding: "10px 14px",
                          fontSize: "0.92rem",
                          fontWeight: 600,
                        },
                        "& .MuiSelect-icon": {
                          top: "calc(50% - 0.5em)",
                          color: canEdit ? ORANGE : "rgba(0, 0, 0, 0.54)",
                        },
                      }}
                      renderValue={selected => {
                        if (!selected || !editStatus) {
                          return (
                            <Typography
                              color="text.secondary"
                              sx={{ fontSize: "0.9rem" }}
                            >
                              {t("platform.selectASuggestion")}
                            </Typography>
                          );
                        }
                        return (
                          <Typography
                            sx={{ fontSize: "0.92rem", fontWeight: 700 }}
                          >
                            {editStatus.longDescription}
                          </Typography>
                        );
                      }}
                    >
                      {customerStatusOptions.map(option => (
                        <MenuItem
                          key={option.code}
                          value={option.code}
                          disabled={
                            !!lead?.customerResponse &&
                            lead?.customerResponse === option.code
                          }
                        >
                          <Box sx={{ py: 0.5 }}>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.9rem",
                                mb: 0.25,
                              }}
                            >
                              {option.longDescription}
                            </Typography>
                            <Typography
                              color="text.secondary"
                              sx={{ fontSize: "0.8rem" }}
                            >
                              {option.shortDescription}
                            </Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {!isEditingNotes ? (
                    <>
                      <Box
                        sx={{
                          p: "12px 0 12px 12px",
                          border: "1px solid #e0e0e0",
                          borderRadius: "12px",
                          backgroundColor: "#f9f9f9",
                          minHeight: 138,
                          display: "flex",
                          alignItems: "baseline",
                          justifyContent: "space-between",
                          mb: 1.5,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: "0.9rem", flex: 1 }}
                          color="#00000099"
                        >
                          {editNotes ? editNotes : t("platform.addNotes")}
                        </Typography>
                        {canEdit && (
                          <IconButton
                            onClick={() => {
                              setIsEditingNotes(true);
                              setTempNotes(editNotes ?? "");
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                      </Box>
                      <Divider sx={{ borderColor: "#F3F5F4" }} />
                    </>
                  ) : (
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: "12px",
                        backgroundColor: "#f9f9f9",
                        overflow: "hidden",
                        mb: 1.5,
                      }}
                    >
                      <TextField
                        multiline
                        rows={4}
                        fullWidth
                        value={tempNotes ?? ""}
                        onChange={e => setTempNotes(e.target.value)}
                        placeholder={t("platform.addYourNotesHere")}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: "transparent",
                            borderRadius: "12px 12px 0 0",
                            padding: 0,
                            "& fieldset": { border: "none" },
                            "&:hover fieldset": { border: "none" },
                            "&.Mui-focused fieldset": { border: "none" },
                          },
                          "& .MuiInputBase-input": {
                            padding: "14px 14px 0",
                            fontSize: "0.9rem",
                            lineHeight: 1.5,
                          },
                        }}
                      />
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 1,
                          p: 1.25,
                          borderTop: "1px solid #e0e0e0",
                          backgroundColor: "#f9f9f9",
                        }}
                      >
                        <Button
                          size="small"
                          onClick={() => {
                            setIsEditingNotes(false);
                            setTempNotes(editNotes ?? "");
                          }}
                          sx={{
                            color: "text.secondary",
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#f5f5f5" },
                          }}
                        >
                          {t("platform.cancel")}
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            onEditNotes(tempNotes);
                            setIsEditingNotes(false);
                          }}
                          sx={{
                            backgroundColor: ORANGE,
                            color: "white",
                            textTransform: "none",
                            "&:hover": { backgroundColor: ORANGE_HOVER },
                          }}
                        >
                          {t("platform.ok")}
                        </Button>
                      </Box>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    disabled={updateDisabled}
                    sx={{
                      position: "absolute",
                      width: "230px",
                      bottom: "10px",
                      left: "15px",
                      backgroundColor:
                        hasChange && leadData ? ORANGE : "#e0e0e0",
                      color: hasChange && leadData ? "white" : "#999",
                      borderRadius: "12px",
                      textTransform: "none",
                      fontWeight: 700,
                      py: 1.25,
                      fontSize: "0.95rem",
                      cursor: hasChange && leadData ? "pointer" : "not-allowed",
                      "&:hover": {
                        backgroundColor:
                          hasChange && leadData ? ORANGE_HOVER : "#e0e0e0",
                      },
                      "&:disabled": {
                        backgroundColor: "#e0e0e0",
                        color: "#999",
                      },
                    }}
                    onClick={onSubmit}
                  >
                    {t("platform.save")}
                  </Button>
                </SectionCard>

                <SectionCard minHeight={300}>
                  <CardHeader title={t("platform.journey")} mb={0.5} />
                  <Typography sx={{ color: MUTED, fontSize: "0.85rem", mb: 3 }}>
                    {lead?.assignedChannel || lead?.channel || "—"}
                  </Typography>
                  <Box sx={{ display: "grid", rowGap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {formatDateToMMDDYYYY(lead?.lastContactDate) ? (
                        <TickInCircleIcon />
                      ) : (
                        <CircleIcon />
                      )}
                      <Box sx={{ gap: 1 }}>
                        <Typography
                          sx={{ fontSize: "0.9rem", fontWeight: 600 }}
                        >
                          {t("platform.lastContactedOn")}
                        </Typography>
                        <Typography
                          sx={{
                            color: MUTED,
                            fontSize: "0.85rem",
                            mt: 1,
                            ml: "auto",
                          }}
                        >
                          {formatDateToMMDDYYYY(lead?.lastContactDate) || "—"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {formatDateToMMDDYYYY(lead?.createdOn) ? (
                        <TickInCircleIcon />
                      ) : (
                        <CircleIcon />
                      )}
                      <Box sx={{ gap: 1 }}>
                        <Typography
                          sx={{ fontSize: "0.9rem", fontWeight: 600 }}
                        >
                          {t("platform.accountCreated")}
                        </Typography>
                        <Typography
                          sx={{
                            color: MUTED,
                            fontSize: "0.85rem",
                            mt: 1,
                            ml: "auto",
                          }}
                        >
                          {formatDateToMMDDYYYY(lead?.createdOn) || "—"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </SectionCard>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default LeadDetailModal;
